'use client'

import { getTokenImageUrl } from '@/domain/euler'
import { getDisplayTokenSymbol } from '@/domain/hooks/useDisplayToken'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import type { ELendVaultExposureSummaryMap } from '@/features/elend-details/logic/useELendVaultExposureSummaries'
import { hasAnyFraxlendPause } from '@/features/isolated/types/FraxlendPairPauseState'
import { type UsePortfolioEvmBalancesResult, getPortfolioHoldingsData } from '@/features/portfolio'
import { assets } from '@/ui/assets'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { FraxlendPauseIndicator } from '@/ui/molecules/fraxlend-pause-indicator/FraxlendPauseIndicator'
import { FrozenPill } from '@/ui/molecules/frozen-pill/FrozenPill'
import { Info } from '@/ui/molecules/info/Info'
import { SupplyApyTooltip } from '@/ui/molecules/supply-apy-tooltip/SupplyApyTooltip'
import { getPointsVisibility } from '@/ui/organisms/market-entry/utils/getPointsVisibility'
import { getPartnerPointKey } from '@/ui/organisms/market-entry/utils/partnerPointsKey'
import { resolvePointsDisplay } from '@/ui/organisms/market-entry/utils/resolvePointsDisplay'
import { ArrowRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import type { YieldOpportunity } from '../types'
import { getMarketType, getPointsType, getUtilization, getYieldOpportunityDetailsPath } from '../utils'
import { HoldingsCellBase } from './HoldingsCell'
import { UtilizationCell } from './UtilizationCell'

interface UnifiedEarnMobileListProps {
  opportunities: YieldOpportunity[]
  elendExposureSummaries?: ELendVaultExposureSummaryMap
  portfolio: UsePortfolioEvmBalancesResult
}

const DEFAULT_CURATOR_LOGO = '/hypurr-paw.svg'

function formatApy(value: number | null | undefined): string {
  if (value == null || value === 0) return '-'
  if (value > 0 && value < 0.005) return '<0.01%'
  return `${value.toFixed(2)}%`
}

function formatUsdCompact(value: number | undefined): string {
  if (value === undefined || value === 0) return '-'
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(2)}`
}

function isCollateralOnlyOpportunity(
  opp: YieldOpportunity,
  elendExposureSummaries?: ELendVaultExposureSummaryMap,
): boolean {
  if (opp.sourceData.type === 'elend') {
    const summary = elendExposureSummaries?.get(opp.address.toLowerCase())
    if (summary) {
      return summary.borrowableCount === 0 && summary.collateralCount > 0
    }
    return opp.sourceData.isCollateralOnly === true
  }
  if (opp.sourceData.type === 'pooled') {
    return opp.sourceData.isCollateralOnly === true
  }
  return false
}

interface MobileOpportunityCardProps {
  opp: YieldOpportunity
  elendExposureSummaries?: ELendVaultExposureSummaryMap
  portfolio: UsePortfolioEvmBalancesResult
}

function MobileOpportunityCard({ opp, elendExposureSummaries, portfolio }: MobileOpportunityCardProps) {
  const { getByAddress } = usePointsData()

  const tokenImage = getTokenImageUrl(opp.tokenInfo, opp.assetSymbol)
  const displaySymbol = getDisplayTokenSymbol(opp.chainId, TokenSymbol(opp.assetSymbol))
  const marketType = getMarketType(opp)
  const fallbackLogo = opp.tokenInfo?.logoURI || opp.token?.logoURI
  const curatorLogo = opp.curatorLogo || DEFAULT_CURATOR_LOGO
  const detailsPath = getYieldOpportunityDetailsPath(opp)
  const isCollateralOnly = isCollateralOnlyOpportunity(opp, elendExposureSummaries)

  const pointsType = getPointsType(opp.source)
  const points = getByAddress({
    type: pointsType,
    action: 'deposit',
    address: opp.address,
  })
  const depositOrLendContextPoints = opp.partnerPoints?.filter((p) => p.lend || p.deposit || p.collateral)
  const { hasHypurrPoints, hasPartnerPoints, isEarnVault } = resolvePointsDisplay({
    entryPartnerPoints: depositOrLendContextPoints,
    pointsInfo: { multiplier: points?.multiplier },
    isEarnVault: opp.source === 'evault',
  })
  const { hasEarnVaultPoints, hasVisiblePoints: hasPoints } = getPointsVisibility({
    hasHypurrPoints,
    hasPartnerPoints,
    isEarnVault,
  })

  const apyBreakdown = (() => {
    if (opp.sourceData.type === 'elend' || opp.sourceData.type === 'pooled') {
      return {
        lendingApy: opp.sourceData.lendingAPY ?? opp.supplyAPY,
        intrinsicApy: opp.sourceData.intrinsicAPY,
        totalApy: opp.supplyAPY,
      }
    }
    return {
      lendingApy: opp.supplyAPY,
      intrinsicApy: undefined,
      totalApy: opp.supplyAPY,
    }
  })()

  const apyContent = <span className="font-medium text-foreground text-sm">{formatApy(opp.supplyAPY)}</span>

  const apyCell = (() => {
    if (isCollateralOnly && opp.supplyAPY <= 0) {
      return (
        <Info size={14} className="cursor-help">
          This asset is collateral only and does not earn borrowing-driven APR.
        </Info>
      )
    }

    if (opp.supplyAPY > 0) {
      return (
        <SupplyApyTooltip
          breakdown={apyBreakdown}
          collateralOnly={isCollateralOnly}
          marketType={opp.source}
          showPointsSection={false}
          vaultAddress={opp.address}
        >
          {apyContent}
        </SupplyApyTooltip>
      )
    }

    return apyContent
  })()

  const pointsIcons = hasPoints ? (
    <div className="flex items-center">
      {depositOrLendContextPoints?.map((partner, idx) => (
        <div
          key={getPartnerPointKey(partner)}
          className="relative h-4 w-4 rounded-full border border-background"
          style={{
            marginLeft: idx > 0 ? '-4px' : '0',
            zIndex: 9 + idx,
          }}
        >
          <Image
            src={assets[partner.icon as keyof typeof assets]}
            alt={partner.title}
            className="h-full w-full rounded-full"
          />
        </div>
      ))}
      {(hasHypurrPoints || hasEarnVaultPoints) && (
        <div
          className="relative h-4 w-4 rounded-full border border-background"
          style={{
            marginLeft: hasPartnerPoints ? '-4px' : '0',
            zIndex: 20,
          }}
        >
          <Image src={assets.hypurrPawNoShadow} alt="HP" className="h-full w-full" />
        </div>
      )}
    </div>
  ) : null

  return (
    <div className="mx-auto w-full max-w-lg rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
      <div className="flex w-full flex-col">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <TokenImage
                src={tokenImage}
                alt={displaySymbol}
                width={32}
                height={32}
                className="h-8 w-8 rounded-xl object-cover"
                fallbackSrc={fallbackLogo}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 overflow-hidden rounded-full border border-card bg-card">
                    <TokenImage src={curatorLogo} alt={opp.curatorName || 'Curator'} width={14} height={14} />
                  </div>
                </TooltipTrigger>
                <TooltipContentShort>{opp.curatorName || 'HypurrFi'}</TooltipContentShort>
              </Tooltip>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate font-semibold text-foreground text-sm leading-tight">{displaySymbol}</span>
                {opp.reserveStatus === 'frozen' && <FrozenPill />}
                {opp.fraxlendPauseState && hasAnyFraxlendPause(opp.fraxlendPauseState) && (
                  <FraxlendPauseIndicator state={opp.fraxlendPauseState} />
                )}
              </div>
              <span className="text-dim-foreground text-xs leading-tight">{marketType}</span>
            </div>
          </div>
          <Link
            href={detailsPath}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-80"
          >
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-2 flex flex-col gap-2 border-white/[0.04] border-t pt-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">APY</span>
            {apyCell}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">30d APY</span>
            <span className="font-medium text-foreground text-sm">{formatApy(opp.supplyApy30d)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">TVL</span>
            <span className="font-medium text-foreground text-sm">{formatUsdCompact(opp.tvlUsd?.toNumber())}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">Util.</span>
            <span className="font-medium text-foreground text-sm">
              <UtilizationCell utilization={getUtilization(opp)} source={opp.source} />
            </span>
          </div>
          {hasPoints && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Points</span>
              {pointsIcons}
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">My Holdings</span>
            <HoldingsCellBase {...getPortfolioHoldingsData(opp, portfolio)} />
          </div>
        </div>
      </div>
    </div>
  )
}

export function UnifiedEarnMobileList({
  opportunities,
  elendExposureSummaries,
  portfolio,
}: UnifiedEarnMobileListProps) {
  if (opportunities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16">
        <p className="text-lg text-white/60">No opportunities found</p>
        <p className="text-sm text-white/40">Try adjusting your filters</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {opportunities.map((opp) => (
        <MobileOpportunityCard
          key={`${opp.id}-${getYieldOpportunityDetailsPath(opp)}-${opp.market?.key ?? opp.sourceData.type}`}
          opp={opp}
          elendExposureSummaries={elendExposureSummaries}
          portfolio={portfolio}
        />
      ))}
    </div>
  )
}
